import {
    Signal,
    getDecimalSeparator,
    getThousandsSeparator,
    isNotNullable,
    mergeSignals,
} from '@bayer-int/portletce-shared';
import type { ActionsUnion } from '@rxsv/core';
import { U, createReducer, fromActions } from '@rxsv/core';
import { difference, isEmpty, isEqual, isString, keyBy, omitBy, pick, isArray } from 'lodash-es';
import { createSelector } from 'reselect';
import { EMPTY, merge, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';

import { createDeepEqualSelector } from 'common/utils';
import type { SettingFormInputDefinition, ShortcutElement } from 'header/types';
import type { AppError } from 'portlet/common/errors';
import { cloneSelector } from 'portlet/common/utils';
import type {
    ClearFilterData,
    FilterData,
    ListenFilter,
    FilterIdToSignalMapping,
    ListenToDefinitions,
    ListenToDefinitionsPerView,
    PortletQueryParams,
    ResolvedListenToDefinitions,
    Setting,
    SettingDefinitionMap,
    SettingDefinitionWithExtraInformation,
    SettingPerViewDictionary,
    SettingValueMap,
    View,
    ViewGroup,
} from 'portlet/models';
import {
    FilterDataArray,
    FilterValue,
    ListenTo,
    PortletQuery,
    SettingDefinition,
    SettingKeys,
    Settings,
    Specification,
    ViewHelper,
} from 'portlet/models';
import type { Dimension, Hierarchy, SettingStack } from 'portlet/services';
import { SettingService } from 'portlet/services';

import { userSelector } from '../auth';
import type { EffectFactory } from '../effects';
import { Selectors as InitSelectors } from '../init';
import type { AppSelector } from '../selectors';
/**
 * This store slice holds all dynamic view configurations, that is Filters and Settings.
 * Both filters and settings can be used to produce signals which are feed to data visualization
 * engine - aclib.
 */
export const initialState = {
    /**
     * list of external filters we accept,
     * it may contain elements that reference current setting values
     */
    observedFilters: [] as ListenToDefinitionsPerView,
    /** values of specific filters, set locally or externally */
    items: [] as FilterDataArray,
    /**
     * values of setting selected by the user OR caused by filter changes (see `applyFilterChange` function)
     * they have the highest priority among all of values
     */
    settings: {} as Partial<SettingValueMap>,
    /**
     * default settings which are calculated dynamically (currently only based on view change, see SettingService.computeNewDefaultSettings)
     * their priority is between static `defaultSettings` and settings obtained from filters
     *
     * using `defaultSettingValueMap` instead of `{}`, so that before portlet initialization completes
     * we have values of settings which are always generated (VIEWID and NO_SORTCUTS) regardless of the spec
     */
    computedDefaultSettings: Specification.defaultSettingValueMap(),
    /** default settings taken from config */
    defaultSettings: Specification.defaultSettingValueMap(),
    /**
     * list of setting definitions - values defined here
     * may not be up to date, because the actual value can be defined in any
     * priority level (settings, items or defaultSettings)
     * in order to get the definition with current value applied, use
     * `settingsFormDefinitions` selector
     */
    definitions: {} as Partial<SettingDefinitionMap>,
    /**
     * Mapping between portlet views and list of settings which are available
     */
    viewSettingDictionary: {} as SettingPerViewDictionary,
    /**
     * indicates wether current settings (after merging with filters and defaults)
     * - have definitions which are up to date (e.g. dynamic list of options in Select type settings)
     * - setting dependencies are resolved, and thus setting state is fully consistent
     * Since resolving setting dependencies is asynchronous, we cannot guarantee that
     * setting state will be consistent at all times (without making writes to the store async)
     * this boolean value allows for assesing if they actually are.
     */
    loading: false,
    /**
     * indicates whether all of the mandatory settings were selected at least once
     * this allows us to render only mandatory settings that were reset
     */
    mandatorySettingsInitialized: false,
    error: null as AppError | null,
    /**
     * read only property set during initialization
     * duplicates value form store/init, but this is the only way it can be
     * accessed in the reducer
     */
    specification: null as Specification | null,
    /** signal overrides */
    signals: [] as Signal[],
    /**
     *  information about the time of fetching data
     *  and the possibility of getting them from the cache
     */
    dataFrom: null as { date: Date; isDataPossiblyCached: boolean } | null,
};

export const Action = U.createUnion(
    U.caseOf('SET_DEFAULT_SETTINGS')(),
    //Action which resets settings and filters. We leave a way to preserve filters or signals.
    //There is a way to introduce signal names to preserve. Empty array is treated as false and will
    //reset all of signals.
    U.caseOf('RESET_SETTINGS_AND_FILTERS')<{
        preserveFilters: boolean;
        preserveSignals: boolean | string[];
    }>(),
    U.caseOf('INIT_FILTERS_AND_SETTINGS')<{
        filters: FilterData[];
        observedFilters: ListenToDefinitionsPerView;
        settings: Partial<SettingValueMap>;
        defaultSettings: Partial<SettingValueMap>;
        computedDefaultSettings: Partial<SettingValueMap>;
        definitions: Partial<SettingDefinitionMap>;
        viewSettingDictionary: SettingPerViewDictionary;
        specification: Specification;
        signals: Signal[];
    }>(),
    U.caseOf('CHANGE_FILTER_VALUE')<FilterData>(),
    U.caseOf('CHANGE_FILTER_VALUES')<FilterDataArray>(),
    U.caseOf('CHANGE_SETTING_VALUE')<Setting>(),
    U.caseOf('CHANGE_SETTING_VALUES')<Setting[]>(),
    U.caseOf('CLEAR_SETTING_VALUES')<(keyof SettingValueMap)[]>(),
    U.caseOf('EMIT_LISTEN_FILTERS')<ListenFilter[]>(),
    U.caseOf('EMIT_SETTINGS_FORM')<SettingDefinitionWithExtraInformation[]>(),
    U.caseOf('SET_FILTER_VALUE')<FilterData>(),
    U.caseOf('SET_FILTER_VALUES')<FilterDataArray>(),
    U.caseOf('SET_ALL_FILTER_VALUES')<FilterDataArray>(),
    U.caseOf('CLEAR_FILTER_REQUEST')<ClearFilterData>(),
    U.caseOf('CLEAR_FILTERS_REQUEST')<ClearFilterData[]>(),
    U.caseOf('CLEAR_FILTER')<ClearFilterData>(),
    U.caseOf('CLEAR_FILTERS')<ClearFilterData[]>(),

    U.caseOf('DEFINITIONS_LOADED')<{
        definitions: Partial<SettingDefinitionMap>;
        stack: SettingStack;
    }>(),
    U.caseOf('DEFINITIONS_ERROR')<AppError>(),
    U.caseOf('FORCE_LOAD_LAZY_SETTING_DEFINITION')<SettingDefinition['key']>(),
    U.caseOf('FORCE_LOAD_LAZY_ALL_SETTING_DEFINITIONS')(),
    U.caseOf('APPLIED_FILTER_VALUES')(),
    U.caseOf('SET_SIGNALS')<Signal[]>(),
    U.caseOf('SET_DATA_FROM')<{
        date: Date;
        isDataPossiblyCached: boolean;
    }>(),
);

export type Action = ActionsUnion<typeof Action>;
export const ErrorActions = [Action.DEFINITIONS_ERROR];
export type State = typeof initialState;

export const reducer = createReducer(initialState)<Action>({
    SET_DEFAULT_SETTINGS: state => {
        return { ...state, settings: {} };
    },
    RESET_SETTINGS_AND_FILTERS: (state, { payload }) => {
        const { signals } = state;
        const preservedSignals = isArray(payload.preserveSignals)
            ? isEmpty(payload.preserveSignals)
                ? []
                : signals.filter(signal => {
                      const signalsToPreserve = payload.preserveSignals as string[];
                      return signalsToPreserve.includes(signal.name);
                  })
            : payload.preserveSignals
              ? signals
              : [];
        if (payload.preserveFilters) {
            const {
                settings: _,
                computedDefaultSettings: __,
                defaultSettings: ___,
                ...rest
            } = state;
            return {
                ...initialState,
                ...rest,
                signals: [...preservedSignals],
            };
        }
        return { ...initialState, signals: [...preservedSignals] };
    },
    INIT_FILTERS_AND_SETTINGS: (
        _,
        {
            payload: {
                filters,
                observedFilters,
                settings,
                defaultSettings,
                computedDefaultSettings,
                definitions,
                viewSettingDictionary,
                specification,
                signals,
            },
        },
    ) => ({
        items: filters,
        observedFilters,
        settings,
        defaultSettings,
        computedDefaultSettings,
        definitions,
        viewSettingDictionary,
        loading: true,
        mandatorySettingsInitialized: false,
        error: null,
        specification,
        signals,
        dataFrom: null,
    }),
    CHANGE_FILTER_VALUE: applyFilterChange,
    CHANGE_FILTER_VALUES: applyMultipleFilterChange,
    SET_FILTER_VALUE: applyFilterChange,
    SET_FILTER_VALUES: applyMultipleFilterChange,
    SET_ALL_FILTER_VALUES: applyAllFilterChange,
    CHANGE_SETTING_VALUE: (state, { payload }) => {
        const settings = Settings.updateSetting(state.settings, payload);

        return {
            ...state,
            loading: true,
            settings,
            mandatorySettingsInitialized:
                state.mandatorySettingsInitialized ||
                areAllMandatorySettingsSet(
                    Settings.merge(state.defaultSettings, state.computedDefaultSettings, settings),
                ),
        };
    },
    CHANGE_SETTING_VALUES: (state, { payload }) => ({
        ...state,
        loading: true,
        settings: payload.reduce(Settings.updateSetting, state.settings),
    }),
    CLEAR_SETTING_VALUES: (state, { payload }) => ({
        ...state,
        loading: true,
        settings: omitBy(state.settings, (_, key) => payload.includes(key)),
    }),
    EMIT_LISTEN_FILTERS: items => items,
    EMIT_SETTINGS_FORM: state => state,
    CLEAR_FILTER_REQUEST: (state, { payload }) => ({
        ...state,
        items: FilterDataArray.clearFilter(state.items, payload),
    }),
    CLEAR_FILTERS_REQUEST: (state, { payload }) => ({
        ...state,
        items: FilterDataArray.clearFilters(state.items, payload),
    }),
    CLEAR_FILTER: (state, { payload }) => ({
        ...state,
        items: FilterDataArray.clearFilter(state.items, payload),
    }),
    CLEAR_FILTERS: (state, { payload }) => ({
        ...state,
        items: FilterDataArray.clearFilters(state.items, payload),
    }),
    DEFINITIONS_LOADED: (state, { payload: { definitions, stack } }) => ({
        ...state,
        loading: false,
        definitions: { ...state.definitions, ...definitions },
        computedDefaultSettings: stack.computedDefaultSettings,
        settings: stack.settings,
    }),
    DEFINITIONS_ERROR: (state, { payload: error }) => ({ ...state, error }),
    FORCE_LOAD_LAZY_SETTING_DEFINITION: (state, { payload }) => {
        const definition = state.definitions[payload];

        return definition
            ? {
                  ...state,
                  definitions: {
                      ...state.definitions,
                      [payload]: SettingService.markLazySettingAsPending(definition),
                  },
              }
            : state;
    },
    FORCE_LOAD_LAZY_ALL_SETTING_DEFINITIONS: state => ({
        ...state,
        definitions: SettingDefinition.mapSettingDefinitionMap(
            state.definitions,
            SettingService.markLazySettingAsPending,
        ),
    }),
    SET_SIGNALS: (state, { payload }) => ({
        ...state,
        signals: mergeSignals(state.signals, payload),
    }),
    SET_DATA_FROM: (state, { payload }) => ({ ...state, dataFrom: payload }),
    APPLIED_FILTER_VALUES: state => state,
});

const specificationSelector: AppSelector<Specification | null> = state =>
    state.filtersSettings.specification;
const allFilterSelector: AppSelector<FilterData[]> = state => state.filtersSettings.items;
const defaultSettingSelector: AppSelector<Partial<SettingValueMap>> = state =>
    state.filtersSettings.defaultSettings;
const computedDefaultSettingSelector: AppSelector<Partial<SettingValueMap>> = state =>
    state.filtersSettings.computedDefaultSettings;
const settingSelector: AppSelector<Partial<SettingValueMap>> = createDeepEqualSelector(
    state => state.filtersSettings.settings,
    settings => settings,
);
const settingDefinition: AppSelector<Partial<SettingDefinitionMap>> = createDeepEqualSelector(
    state => state.filtersSettings.definitions,
    definitions => definitions,
);
const settingsPerViewDictionary: AppSelector<SettingPerViewDictionary> = state =>
    state.filtersSettings.viewSettingDictionary;
const rawObservedFiltersPerView: AppSelector<ListenToDefinitionsPerView> = state =>
    state.filtersSettings.observedFilters;
const areSettingsConsistent: AppSelector<boolean> = state => !state.filtersSettings.loading;
const areMandatorySettingsInitialized: AppSelector<boolean> = state =>
    state.filtersSettings.mandatorySettingsInitialized;

const settingsWithDefaults: AppSelector<Partial<SettingValueMap>> = createSelector(
    defaultSettingSelector,
    computedDefaultSettingSelector,
    settingSelector,
    Settings.merge,
);

const rawObservedFilters: AppSelector<ListenToDefinitions> = createSelector(
    rawObservedFiltersPerView,
    observedFiltersPerView => observedFiltersPerView.map(({ definition }) => definition),
);

const rawObservedFiltersOnCurrentView: AppSelector<ListenToDefinitions> = createSelector(
    rawObservedFiltersPerView,
    settingsWithDefaults,
    (observedFiltersPerView, settings) => {
        const viewListenTos = observedFiltersPerView.filter(
            ({ viewId }) => viewId === settings.VIEWID,
        );
        const mainConfigListenTos = observedFiltersPerView.filter(({ viewId }) => viewId === null);
        const sortedListenTos = [...viewListenTos, ...mainConfigListenTos];

        return sortedListenTos.map(({ definition }) => definition);
    },
);

const listenTo: AppSelector<ResolvedListenToDefinitions> = createSelector(
    rawObservedFilters,
    settingsWithDefaults,
    ListenTo.materializeListenTo,
);

const listenToOnCurrentView: AppSelector<ResolvedListenToDefinitions> = createSelector(
    rawObservedFiltersOnCurrentView,
    settingsWithDefaults,
    ListenTo.materializeListenTo,
);

const observedFilters: AppSelector<ListenFilter[]> = createSelector(listenTo, listenTo =>
    listenTo.map(({ item, interactionEnabled }) => {
        return { filterId: item, isInteractionEnabled: interactionEnabled };
    }),
);

const filterToSignalNameMapping: AppSelector<FilterIdToSignalMapping> = createSelector(
    listenTo,
    ListenTo.toFilterNameMapping,
);

const filterSelector: AppSelector<FilterData[]> = createDeepEqualSelector(
    allFilterSelector,
    observedFilters,
    (filters, observed) =>
        filters.filter(({ item }) => observed.map(({ filterId }) => filterId).includes(item)),
);

const settingStackSelector: AppSelector<SettingStack> = createSelector(
    defaultSettingSelector,
    computedDefaultSettingSelector,
    settingSelector,
    (defaultSettings, computedDefaultSettings, settings) => ({
        defaultSettings,
        computedDefaultSettings,
        settings,
    }),
);

const settingsWithFilters: AppSelector<Partial<SettingValueMap>> = createSelector(
    defaultSettingSelector,
    computedDefaultSettingSelector,
    settingSelector,
    Settings.merge,
);

const currentViewId: AppSelector<number | null> = createSelector(
    settingsWithFilters,
    settings => settings.VIEWID ?? null,
);

const currentView: AppSelector<View | null> = createSelector(
    currentViewId,
    specificationSelector,
    (currentViewId, spec) => {
        return spec && currentViewId !== null
            ? Specification.getViewById(spec, currentViewId)
            : null;
    },
);

const currentViewGroup: AppSelector<ViewGroup | null> = createSelector(
    specificationSelector,
    currentView,
    (spec, view) => (spec && view ? ViewHelper.getViewGroup(spec, view) : null),
);

const settingsDefinitionsOnCurrentView: AppSelector<Partial<SettingDefinitionMap>> = createSelector(
    settingDefinition,
    settingsPerViewDictionary,
    currentViewId,
    (definitions, viewDictionary, viewId) =>
        viewId !== null && viewDictionary[viewId]
            ? pick(definitions, viewDictionary[viewId])
            : definitions,
);

const currentViewSignals: AppSelector<Signal[]> = createSelector(
    specificationSelector,
    currentView,
    (spec, view) => [...(spec?.signals || []), ...(view?.signals || [])],
);

const signalsSelector: AppSelector<Signal[]> = state => state.filtersSettings.signals;

const queryMetadataSelector: AppSelector<PortletQuery | null> = createSelector(
    specificationSelector,
    currentViewId,
    (spec, viewId) => {
        if (!spec || viewId === null) return null;

        const query = spec.views.find(({ id }) => id === viewId)?.query;
        const output = query || spec.query;

        return !output ? null : output;
    },
);

const querySelector: AppSelector<PortletQueryParams | null> = createSelector(
    currentViewSignals,
    filterSelector,
    settingsWithFilters,
    queryMetadataSelector,
    filterToSignalNameMapping,
    (signals, filters, settings, queryMetadata, listenToSignalMapping) => {
        if (!queryMetadata) return null;

        return PortletQuery.build(queryMetadata, settings, filters, signals, listenToSignalMapping);
    },
);

const queryLinkSelector: AppSelector<string | null> = createSelector(
    currentViewSignals,
    filterSelector,
    settingsWithFilters,
    queryMetadataSelector,
    filterToSignalNameMapping,
    (signals, filters, settings, queryMetadata, listenToSignalMapping) => {
        if (!queryMetadata) return null;

        return PortletQuery.externalLink(
            queryMetadata,
            settings,
            filters,
            signals,
            listenToSignalMapping,
        );
    },
);

const querySignals: AppSelector<Signal[]> = createSelector(querySelector, query => {
    return query ? [{ name: 'QUERY', value: query }] : [];
});

const userSignals: AppSelector<Signal[]> = createSelector(
    userSelector,
    InitSelectors.configSelector,
    (user, config) =>
        [
            user && {
                name: 'CWID',
                value: user.cwid,
            },
            config && {
                name: 'LOCALIZATION',
                value: {
                    locale: config.locale,
                    decimalSeparator: getDecimalSeparator(config.locale),
                    thousandSeparator: getThousandsSeparator(config.locale),
                },
            },
        ].filter(isNotNullable),
);

const filterSignals: AppSelector<Signal[]> = createSelector(
    settingsWithDefaults,
    filterSelector,
    filterToSignalNameMapping,
    (settings, filters, filterToSignalName) => {
        const filterSignals: Signal[] = filters.map(({ item, value }) => ({
            name: filterToSignalName[item] || item,
            value: FilterValue.match<number | string | string[] | unknown>(value, {
                DATE: ({ value }) => value,
                DATE_RANGE: ({ value }) => value,
                DIMENSION: ({ value }) => value,
                SCALE: ({ value }) => Number.parseInt(value.key, 10),
                HIERARCHY: ({ value: filterValue }) =>
                    filterValue.values.map(
                        ({
                            key,
                            identifier,
                            keyPathToRoot,
                            identifierPathToRoot,
                            valuePathToRoot,
                            value,
                            effect,
                        }) => ({
                            key,
                            value,
                            identifier,
                            keyPathToRoot,
                            identifierPathToRoot,
                            valuePathToRoot,
                            internalKey: settings.INTERNAL_KEY,
                            internalKeys: filterValue.hierarchyInternalKeys,
                            effect,
                        }),
                    ),
                SMART_VIEW_TAG: ({ viewTag }) => viewTag,
                ROLE: ({ value }) => value.key,
                NUMBER: ({ value }) => value,
            }),
        }));

        /**
         * Due to the fact that `filterToSignalName` mapping may map multiple values to the same name,
         * we group them into a single signal with array value.
         */
        return Signal.groupByName(filterSignals);
    },
);

const settingSignals: AppSelector<{
    defaultSignals: Signal[];
    computedDefaultSignals: Signal[];
    settingSignals: Signal[];
}> = createSelector(
    settingDefinition,
    defaultSettingSelector,
    computedDefaultSettingSelector,
    settingSelector,
    filterSelector,
    filterToSignalNameMapping,
    settingsWithDefaults,
    currentViewSignals,
    queryMetadataSelector,
    (
        settingDefinitions,
        defaultSettings,
        computedDefaultSettings,
        settings,
        filters,
        filterToSignalName,
        mergedSettings,
        currentViewSignals,
        queryMetadata,
    ) => {
        const defaultSignals: Signal[] = Settings.toSignals(
            defaultSettings,
            settingDefinitions,
            mergedSettings,
            currentViewSignals,
            filters,
            queryMetadata,
            filterToSignalName,
        );

        const computedDefaultSignals: Signal[] = Settings.toSignals(
            computedDefaultSettings,
            settingDefinitions,
            mergedSettings,
            currentViewSignals,
            filters,
            queryMetadata,
            filterToSignalName,
        );

        const settingSignals: Signal[] = Settings.toSignals(
            settings,
            settingDefinitions,
            mergedSettings,
            currentViewSignals,
            filters,
            queryMetadata,
            filterToSignalName,
        );

        return { defaultSignals, computedDefaultSignals, settingSignals };
    },
);

const toSignals: AppSelector<Signal[]> = createSelector(
    settingSignals,
    filterSignals,
    querySignals,
    signalsSelector,
    userSignals,
    (
        { defaultSignals, computedDefaultSignals, settingSignals },
        groupedFilterSignals,
        querySignals,
        signalOverrides,
        userSignals,
    ) => {
        return mergeSignals(
            defaultSignals,
            computedDefaultSignals,
            userSignals,
            querySignals,
            groupedFilterSignals,
            settingSignals,
            signalOverrides,
        );
    },
);

const settingsFormDefinition: AppSelector<Partial<SettingDefinitionMap>> = createSelector(
    settingsDefinitionsOnCurrentView,
    settingsWithFilters,
    Settings.matchDefinitionsWithValues,
);

const settingsFormDefinitions: AppSelector<SettingFormInputDefinition[]> = createSelector(
    settingsFormDefinition,
    SettingDefinition.toListOfDefinitions,
);

const currentDimensionSetting: AppSelector<Dimension | null> = createSelector(
    settingDefinition,
    settingsWithFilters,
    (definitions, settings) => {
        if (!definitions.INTERNAL_KEY || !settings.INTERNAL_KEY) return null;

        return SettingDefinition.findOptionOfSelectSetting(
            definitions.INTERNAL_KEY,
            settings.INTERNAL_KEY,
        );
    },
);

const currentHierarchySetting: AppSelector<Hierarchy | null> = createSelector(
    settingDefinition,
    settingsWithFilters,
    (definitions, settings) => {
        if (!definitions.HIERARCHY_ID || !settings.HIERARCHY_ID) return null;

        return SettingDefinition.findOptionOfSelectSetting(
            definitions.HIERARCHY_ID,
            settings.HIERARCHY_ID,
        );
    },
);

const currentLinkedHierarchiesSetting: AppSelector<Record<Hierarchy['hierarchyId'], Hierarchy>> =
    createSelector(settingDefinition, settingsWithFilters, (definitions, settings) => {
        if (!definitions.LINKED_HIERARCHY_IDS || !settings.LINKED_HIERARCHY_IDS) return {};

        const hierarchies = SettingDefinition.findOptionOfMultiSelectSetting(
            definitions.LINKED_HIERARCHY_IDS,
            settings.LINKED_HIERARCHY_IDS,
        );

        return keyBy(hierarchies, ({ hierarchyId }) => hierarchyId);
    });

const currentApplyFilterOnExactDrillOnlySetting: AppSelector<boolean | null> = createSelector(
    settingsWithFilters,
    settings => {
        return settings.APPLY_FILTER_ON_EXACT_DRILL_ONLY ?? null;
    },
);

const currentApplyFilterOnSelectionSetting: AppSelector<boolean | null> = createSelector(
    settingsWithFilters,
    settings => settings.APPLY_FILTER_ON_SELECTION ?? null,
);

const currentApplyFilterToUpstreamHierarchyLevelsSetting: AppSelector<boolean | null> =
    createSelector(
        settingsWithFilters,
        settings => settings.APPLY_FILTER_TO_UPSTREAM_HIERARCHY_LEVELS ?? null,
    );

const currentMandatorySettings: AppSelector<Array<string>> = createSelector(
    settingDefinition,
    settingsWithFilters,
    (definitions, settings) => {
        if (!definitions.MANDATORY_SETTINGS || !settings.MANDATORY_SETTINGS) return [];

        return SettingDefinition.findOptionOfMultiSelectSetting(
            definitions.MANDATORY_SETTINGS,
            settings.MANDATORY_SETTINGS,
        ).map(({ key }) => key);
    },
);

const currentResettableSettings: AppSelector<Array<string>> = createSelector(
    settingDefinition,
    settingsWithFilters,
    (definitions, settings) => {
        if (!definitions.RESETTABLE_SETTINGS || !settings.RESETTABLE_SETTINGS) return [];

        return SettingDefinition.findOptionOfMultiSelectSetting(
            definitions.RESETTABLE_SETTINGS,
            settings.RESETTABLE_SETTINGS,
        ).map(({ key }) => key);
    },
);

const mandatorySettingsFormDefinitions: AppSelector<SettingFormInputDefinition[]> = createSelector(
    settingsFormDefinitions,
    currentMandatorySettings,
    (definitions, settings) => definitions.filter(definition => settings.includes(definition.key)),
);

const nonDefaultMandatorySettingsFormDefinitions: AppSelector<SettingFormInputDefinition[]> =
    createSelector(
        settingsFormDefinitions,
        currentMandatorySettings,
        defaultSettingSelector,
        (definitions, settings, defaults) => {
            return definitions.filter(
                definition =>
                    settings.includes(definition.key) && defaults[definition.key] === null,
            );
        },
    );

const nonEmptyMandatorySettingsFormDefinitions: AppSelector<SettingFormInputDefinition[]> =
    createSelector(nonDefaultMandatorySettingsFormDefinitions, settings =>
        settings.filter(setting => !setting.value),
    );

const resettableSettingsFormDefinitions: AppSelector<SettingFormInputDefinition[]> = createSelector(
    settingsFormDefinitions,
    currentResettableSettings,
    (definitions, settings) => definitions.filter(definition => settings.includes(definition.key)),
);

const uninitializedLazySettingDefinitions: AppSelector<Array<SettingFormInputDefinition['key']>> =
    createSelector(settingsFormDefinitions, definitions =>
        Object.values(definitions)
            .filter(({ lazy }) => lazy === 'uninitialized')
            .map(({ key }) => key),
    );

const displayedShortcuts: AppSelector<SettingValueMap['SHORTCUTS']> = createSelector(
    currentViewGroup,
    settingsWithDefaults,
    (viewGroup, { SHORTCUTS }) => {
        const shortcuts = SHORTCUTS || {
            left: [],
            center: [],
            right: [],
        };

        if (!viewGroup) return shortcuts;

        const availableForms = viewGroup.members.map(({ form }) => form.toLowerCase());

        // filter out VIEW_FORM shortcuts which are not applicable to current view
        const isViewFormValid = (element: ShortcutElement): boolean =>
            !(
                element.key === SettingKeys.VIEW_FORM &&
                isString(element.value) &&
                !availableForms.includes(element.value)
            );

        return {
            left: shortcuts.left.filter(isViewFormValid),
            center: shortcuts.center.filter(isViewFormValid),
            right: shortcuts.right.filter(isViewFormValid),
        };
    },
);

export const Selectors = {
    allFilterSelector,
    filterSelector,
    settingsFormDefinition,
    settingsFormDefinitions,
    toSignals,
    settingSelector,
    settingsWithFilters,
    settingsWithDefaults,
    currentView,
    settingStackSelector,
    currentViewId,
    currentViewSignals,
    observedFilters,
    listenToOnCurrentView,
    currentDimensionSetting,
    currentHierarchySetting,
    currentLinkedHierarchiesSetting,
    areSettingsConsistent,
    areMandatorySettingsInitialized,
    currentMandatorySettings,
    mandatorySettingsFormDefinitions,
    nonDefaultMandatorySettingsFormDefinitions,
    nonEmptyMandatorySettingsFormDefinitions,
    currentResettableSettings,
    resettableSettingsFormDefinitions,
    queryMetadataSelector,
    querySelector,
    queryLinkSelector,
    signalsSelector,
    displayedShortcuts,
    currentApplyFilterOnSelectionSetting,
    currentApplyFilterOnExactDrillOnlySetting,
    currentApplyFilterToUpstreamHierarchyLevelsSetting,
};

export const effect: EffectFactory =
    ({ settingService }) =>
    (action$, state$) => {
        const prevStackSelector = cloneSelector(Selectors.settingStackSelector);
        const currentAndPreviousState = state$.pipe(
            pairwise(),
            map(([previous, state]) => ({ state, previous })),
        );
        const previousConsistentState = state$.pipe(filter(areSettingsConsistent));
        // Takes a consistent state after applying all dependencies.
        // This prevents us from having an unfinished (temporary) state that is still changing,
        // for example, in the applySettingDependenciesAndReloadDefinitions function.
        // Example of incorrect temporary state:
        // settings: { SMART_VIEW: 'TA', SMART_VIEW_VALUES: ['PY', 'BUD', 'MR'] } (smart view is changed but its values are not yet updated)
        const currentAndPreviousConsistentState = state$.pipe(
            withLatestFrom(previousConsistentState),
            map(([state, previous]) => ({ state, previous })),
        );
        const filterOrSettingChange$ = action$.pipe(
            fromActions(
                Action.INIT_FILTERS_AND_SETTINGS,
                Action.CHANGE_SETTING_VALUE,
                Action.CHANGE_SETTING_VALUES,
                Action.CHANGE_FILTER_VALUE,
                Action.CHANGE_FILTER_VALUES,
                Action.SET_FILTER_VALUE,
                Action.SET_FILTER_VALUES,
                Action.SET_ALL_FILTER_VALUES,
                Action.CLEAR_FILTER,
                Action.CLEAR_FILTER,
                Action.SET_DEFAULT_SETTINGS,
            ),
        );
        const forceLoadLazySetting$ = action$.pipe(
            fromActions(
                Action.FORCE_LOAD_LAZY_SETTING_DEFINITION,
                Action.FORCE_LOAD_LAZY_ALL_SETTING_DEFINITIONS,
            ),
            withLatestFrom(currentAndPreviousState),
            filter(([_, { state, previous }]) => {
                const previouslyUninitialized = uninitializedLazySettingDefinitions(previous);
                const currentlyUninitialized = uninitializedLazySettingDefinitions(state);
                const settingsThatBecameInitialized = difference(
                    previouslyUninitialized,
                    currentlyUninitialized,
                );

                return settingsThatBecameInitialized.length > 0;
            }),
        );
        const definitionsLoaded$ = action$.pipe(fromActions(Action.DEFINITIONS_LOADED));

        const onSettingChange$ = merge(filterOrSettingChange$, forceLoadLazySetting$).pipe(
            withLatestFrom(currentAndPreviousConsistentState),
            switchMap(([_, { state, previous }]) => {
                const specification = specificationSelector(state);

                // ignore until portlet is initialized
                if (!specification) return EMPTY;

                const definitions = Selectors.settingsFormDefinition(state);
                const stack = Selectors.settingStackSelector(state);
                const previousStack = prevStackSelector(previous);
                const filters = filterSelector(state) || [];
                const listenToSignalMapping = filterToSignalNameMapping(state);

                return settingService
                    .applySettingDependenciesAndReloadDefinitions(
                        previousStack,
                        stack,
                        specification,
                        definitions,
                        filters,
                        listenToSignalMapping,
                    )
                    .pipe(
                        map(Action.DEFINITIONS_LOADED),
                        catchError(err => of(Action.DEFINITIONS_ERROR(err))),
                    );
            }),
        );

        const emitListenFilters$ = definitionsLoaded$.pipe(
            withLatestFrom(state$),
            map(([_, state]) => Selectors.observedFilters(state)),
            distinctUntilChanged(isEqual),
            map(Action.EMIT_LISTEN_FILTERS),
        );

        const emitSettingsChanged$ = definitionsLoaded$.pipe(
            withLatestFrom(state$),
            map(([_, state]) => {
                const formDefinitions = Selectors.settingsFormDefinitions(state);
                return formDefinitions.map(setting =>
                    SettingDefinition.applyInformationAboutSetting(
                        setting,
                        state.filtersSettings.settings,
                        state.filtersSettings.computedDefaultSettings,
                    ),
                );
            }),
            distinctUntilChanged(isEqual),
            map(Action.EMIT_SETTINGS_FORM),
        );
        return merge(onSettingChange$, emitListenFilters$, emitSettingsChanged$);
    };

function applyFilterChange(
    state: State,
    { payload }: Action & { type: 'CHANGE_FILTER_VALUE' | 'SET_FILTER_VALUE' },
): State {
    const currentSettingsMerged = {
        ...state.defaultSettings,
        ...state.computedDefaultSettings,
        ...state.settings,
    };

    const items = FilterDataArray.updateFilter(state.items, payload);

    const settingsFromFilters = state.specification
        ? Settings.toFilterableSettings(
              state.specification,
              payload,
              state.items,
              currentSettingsMerged,
              state.definitions,
          )
        : {};

    const settings = Settings.merge(state.settings, settingsFromFilters);

    return { ...state, loading: true, items, settings };
}

function applyMultipleFilterChange(
    state: State,
    { payload }: Extract<Action, { type: 'SET_FILTER_VALUES' | 'CHANGE_FILTER_VALUES' }>,
): State {
    const currentSettingsMerged = {
        ...state.defaultSettings,
        ...state.computedDefaultSettings,
        ...state.settings,
    };

    const items = payload.reduce(FilterDataArray.updateFilter, state.items);

    const settingsFromFilters = items.map(filter =>
        state.specification
            ? Settings.toFilterableSettings(
                  state.specification,
                  filter,
                  state.items,
                  currentSettingsMerged,
                  state.definitions,
              )
            : {},
    );

    const settings = Settings.merge(state.settings, ...settingsFromFilters);

    return { ...state, loading: true, items, settings };
}

function applyAllFilterChange(
    state: State,
    { payload: items }: Extract<Action, { type: 'SET_ALL_FILTER_VALUES' }>,
): State {
    const currentSettingsMerged = {
        ...state.defaultSettings,
        ...state.computedDefaultSettings,
        ...state.settings,
    };

    const settingsFromFilters = items.map(filter =>
        state.specification
            ? Settings.toFilterableSettings(
                  state.specification,
                  filter,
                  state.items,
                  currentSettingsMerged,
                  state.definitions,
              )
            : {},
    );

    const settings = Settings.merge(state.settings, ...settingsFromFilters);

    return { ...state, loading: true, items, settings };
}

function areAllMandatorySettingsSet(settings: Partial<SettingValueMap>): boolean {
    return Settings.toSettingList(settings)
        .filter(({ key }) => settings.MANDATORY_SETTINGS?.includes(key as string))
        .every(({ value }) => value !== null);
}
